import React from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { typeColors, TypeVariant } from './types';
import { ChevronDownIcon } from '../../icons';
import type { IconProps } from '../../icons/types';
import { colors } from '../../tokens';

export interface TypeIconTextProps {
  /**
   * The type variant that determines the color
   */
  variant: TypeVariant;

  /**
   * Optional custom icon component
   */
  icon?: React.ComponentType<IconProps>;

  /**
   * Whether to show the chevron down icon
   */
  showChevron?: boolean;

  /**
   * Callback when pressed
   */
  onPress?: () => void;

  /**
   * Whether the component is disabled
   */
  disabled?: boolean;

  /**
   * Test ID for testing
   */
  testID?: string;
}

export const TypeIconText: React.FC<TypeIconTextProps> = ({
  variant,
  icon: Icon,
  showChevron = true,
  onPress,
  disabled = false,
  testID,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const backgroundColor = typeColors[variant];

  const content = (
    <View
      style={[
        styles.container,
        {
          backgroundColor,
          opacity: disabled ? 0.5 : isHovered ? 0.9 : 1,
        },
      ]}
    >
      {Icon && (
        <View style={styles.iconContainer}>
          <Icon size={16} color={colors.brand.white} />
        </View>
      )}
      {showChevron && (
        <View style={styles.chevronContainer}>
          <ChevronDownIcon size={16} color={colors.brand.white} />
        </View>
      )}
    </View>
  );

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        testID={testID}
        accessibilityRole="button"
        accessibilityState={{ disabled }}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <View testID={testID} accessibilityRole="image">
      {content}
    </View>
  );
};

TypeIconText.displayName = 'TypeIconText';

const styles = StyleSheet.create({
  container: {
    width: 40,
    height: 40,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  iconContainer: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chevronContainer: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
